import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaPhoneAlt, FaGoogle } from 'react-icons/fa';
import Hero from '../../components/Hero/Hero';
import Seo from '../../components/Seo/Seo';
import ServiceCard from '../../components/ServiceCard/ServiceCard';
import TestimonialCard from '../../components/TestimonialCard/TestimonialCard';
import { getApprovedFeedback } from '../../utils/api';
import services from '../../utils/services';

const pestKeywords = [
  'pest',
  'cockroach',
  'cockroaches',
  'rodent',
  'rodents',
  // 'termite',
  // 'termites',
  'bed bug',
  'bedbugs',
  'ants',
  'spider',
  'spiders',
];

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);

  const rankTestimonial = (testimonial) => {
    const text = `${testimonial?.message || ''} ${testimonial?.service || ''}`.toLowerCase();
    return pestKeywords.some((keyword) => text.includes(keyword)) ? 1 : 0;
  };

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const data = await getApprovedFeedback();
        const sortedTestimonials = [...(data.feedback || [])].sort((a, b) => {
          const scoreDiff = rankTestimonial(b) - rankTestimonial(a);
          if (scoreDiff !== 0) return scoreDiff;

          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA;
        });

        setTestimonials(sortedTestimonials.slice(0, 3));
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoadingTestimonials(false);
      }
    };

    fetchTestimonials();
  }, []);

  const stats = [
    { number: '10+', label: 'Years Experience' },
    { number: '5,000+', label: 'Happy Customers' },
    { number: String(services.length), label: 'Services Offered' },
    { number: '100%', label: 'Satisfaction Rate' },
  ];

  return (
    <div>
      <Seo
        title="Pest Control Brisbane"
        description="The Skilled Group provides pest control, carpet cleaning, couch cleaning, mattress cleaning, and tile and grout cleaning across Brisbane."
        canonicalPath="/"
      />
      {/* Hero Section */}
      <Hero />

      {/* Stats Bar */}
      <section className="bg-white border-b border-gray-100">
        <div className="container-custom py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1">
                  {stat.number}
                </p>
                <p className="text-gray-500 text-sm font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block bg-primary-50 text-primary-500 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              Pest Control First
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-800 mb-4">
              Pest Control Is Our Core Service
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-lg">
              We are primarily focused on fast, safe, and reliable pest control across Brisbane.
              Our tile and grout, carpet, couch, and mattress steam cleaning services are provided
              as supporting home care solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <div key={service.id} className={index >= 3 ? 'hidden sm:block' : ''}>
                <ServiceCard service={service} index={index} />
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/services"
              className="btn-outline inline-flex items-center gap-2"
            >
              View All Services
              <FaArrowRight className="text-sm" />
            </Link>
          </div>

          {/* Pest Highlight Section */}
          <div className="max-w-4xl mx-auto text-center mt-14 md:mt-16">
            <span className="inline-block bg-accent-100 text-accent-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              Pest Control Priority
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-800 mb-4">
              Brisbane's Fast, Targeted Pest Solutions
            </h2>
            <p className="text-gray-500 text-lg max-w-3xl mx-auto mb-8 leading-relaxed">
              Pest Control is our core focus. We treat common household and commercial infestations quickly,
              then support your property with our steam cleaning services when needed across Brisbane.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              {['Cockroaches', 'Rodents', 'Bed Bugs', 'Ants', 'Spiders'].map((pest) => (
                <span
                  key={pest}
                  className="inline-flex items-center rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700"
                >
                  {pest}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 text-left">
              <div className="rounded-xl border border-gray-100 bg-white p-5">
                <p className="text-xs font-semibold tracking-wide text-primary-500 mb-2">STEP 1</p>
                <h3 className="text-lg font-heading font-semibold text-gray-800 mb-1">Inspect</h3>
                <p className="text-gray-500 text-sm">We identify pest type, hotspots, and risk points across your property.</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-5">
                <p className="text-xs font-semibold tracking-wide text-primary-500 mb-2">STEP 2</p>
                <h3 className="text-lg font-heading font-semibold text-gray-800 mb-1">Treat</h3>
                <p className="text-gray-500 text-sm">Targeted, licensed treatment is applied with safety-first methods.</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-5">
                <p className="text-xs font-semibold tracking-wide text-primary-500 mb-2">STEP 3</p>
                <h3 className="text-lg font-heading font-semibold text-gray-800 mb-1">Follow-Up Advice</h3>
                <p className="text-gray-500 text-sm">We share practical prevention guidance to reduce recurring issues.</p>
              </div>
            </div>

            <Link to="/booking" className="btn-primary inline-flex items-center gap-2">
              Book Pest Inspection
              <FaArrowRight className="text-sm" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="mx-auto mb-10 h-1 w-full max-w-4xl rounded-full bg-primary-500/70 shadow-sm" />
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block bg-primary-50 text-primary-500 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              Testimonials
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-800 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-lg">
              Don't just take our word for it — hear from our satisfied customers
              across Brisbane.
            </p>
          </div>
          {loadingTestimonials ? (
            <div className="flex justify-center py-12">
              <svg className="animate-spin h-10 w-10 text-primary-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
          ) : testimonials.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {testimonials.map((testimonial) => (
                <TestimonialCard key={testimonial._id} testimonial={testimonial} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No testimonials yet. Be the first to share your experience!
              </p>
            </div>
          )}

          <div className="text-center mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/testimonials" className="btn-outline inline-flex items-center gap-2">
              View All Testimonials
              <FaArrowRight className="text-sm" />
            </Link>
            <Link to="/feedback" className="btn-primary inline-flex items-center gap-2">
              Leave Feedback
              <FaArrowRight className="text-sm" />
            </Link>
            <a
              href="https://g.page/r/CQAwFW3XDNHXEBM/review"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline inline-flex items-center gap-2"
              aria-label="Give us a Google Review"
            >
              <FaGoogle className="text-sm" />
              Give us a Google Review
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary-50 via-white to-white py-16 md:py-20">
        <div className="absolute inset-x-0 top-0 h-1 bg-primary-500" />
        <div className="container-custom text-center relative">
          <div className="mx-auto mb-6 h-1 w-20 rounded-full bg-accent-500" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-800 mb-4">
            Ready for a Spotless Clean?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Book your professional cleaning service today and experience the
            The Skilled Group difference. Same-day service available across Brisbane.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" className="btn-accent inline-flex items-center justify-center gap-2">
              Book Online Now
              <FaArrowRight className="text-sm" />
            </Link>
            <a
              href="tel:0466708090"
              className="btn-outline border-primary-300 text-primary-600 hover:bg-primary-500 hover:text-white inline-flex items-center justify-center gap-2"
            >
              <FaPhoneAlt className="text-sm" />
              Call 0466708090
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;